import type { TenantConfig } from '@/types'
import { readonly, ref, shallowRef } from 'vue'
import { activateCompanyStorage, companySessionStorage, clearCompanySessionStorage } from '@/utils/companyStorage'

const configState = shallowRef<TenantConfig | null>(null)
const TENANT_ID_KEY = 'salesRepApp_tenantId'
const COMPANY_NAME_KEY = 'salesRepApp_companyName'
export const companyAccessError = ref('')

/** Returns the last company for an explicit Continue action, without selecting it automatically. */
export function rememberedCompany(): { tenantId: string; name: string } | null {
  const tenantId = localStorage.getItem(TENANT_ID_KEY)
  return tenantId ? { tenantId, name: localStorage.getItem(COMPANY_NAME_KEY) || tenantId } : null
}

/** Loads only an explicit selector; local dev configuration is restricted to Vite development. */
export async function loadServerConfig(selector?: { t?: string; urlName?: string }): Promise<TenantConfig | null> {
  let config: TenantConfig | null = null
  if (import.meta.env.DEV) {
    const raw = localStorage.getItem('salesRepApp_config')
    if (raw) {
      try {
        const dev = JSON.parse(raw) as TenantConfig
        dev.urlName ||= dev.tenantId.replace(/[^a-zA-Z0-9_-]/g, '')
        dev.companyName ||= dev.portalName || dev.tenantId
        if (!selector || selector.t === dev.tenantId || selector.urlName?.toLowerCase() === dev.urlName.toLowerCase()) config = dev
      } catch { /* An invalid local development seed is not a tenant configuration. */ }
    }
  }
  if (!config && selector) {
    const params = new URLSearchParams(selector as Record<string, string>)
    const response = await fetch('/api/tenant-config?' + params.toString(), { cache: 'no-store' })
    if (!response.ok) {
      const failure = await response.json().catch(() => ({})) as { error?: string }
      throw new Error(failure.error || 'Company access is temporarily unavailable. Please retry.')
    }
    config = await response.json() as TenantConfig
  }
  if (!config) return null
  if (!config.tenantId || !config.apiKey || !config.apiBaseUrl || !/^[a-zA-Z0-9_-]{1,100}$/.test(config.urlName)) {
    throw new Error('Company access is being updated. Please try again shortly.')
  }
  await retireLegacySession(config)
  activateCompanyStorage(config.tenantId)
  configState.value = config
  sessionTokenState.value = companySessionStorage.getItem(SESSION_TOKEN_KEY)
  localStorage.setItem(TENANT_ID_KEY, config.tenantId)
  localStorage.setItem(COMPANY_NAME_KEY, config.companyName || config.tenantId)
  return config
}

/** Retires unscoped browser data once; only a tenant-validated, unused legacy token is logged out. */
async function retireLegacySession(config: TenantConfig): Promise<void> {
  const token = sessionStorage.getItem('salesRepApp_sessionToken')
  const previousTenant = localStorage.getItem(TENANT_ID_KEY)
  const legacyKeys = ['salesRepApp_sessionToken', 'salesRepApp_salesRep', 'newOrderState', 'accounts-search-state',
    'salesRepApp_dashboardMetricState', 'salesRepApp_dealBoardScroll', 'salesRepApp_dealPageScroll', 'salesRepApp_dealBoardSort']
  for (const key of legacyKeys) sessionStorage.removeItem(key)
  if (!token || previousTenant !== config.tenantId) return
  // A copied tab can inherit the original tab's ID; never remove a still-live legacy entry.
  try {
    const tabs = JSON.parse(localStorage.getItem('salesRepApp_activeTabs_' + token) || '{}') as Record<string, number>
    if (Object.values(tabs).some(time => typeof time === 'number' && time > Date.now() - 45000)) return
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 5000)
    try {
      await fetch((import.meta.env.DEV ? '' : config.apiBaseUrl) + '/sr/auth/logout', {
        method: 'POST', signal: controller.signal,
        headers: { 'X-Api-Key': config.apiKey, 'X-Tenant-Id': config.tenantId, 'X-Sales-Rep-Token': token }
      })
    } finally { window.clearTimeout(timeout) }
  } catch { /* Never retain an ambiguous old token; existing license recovery remains available. */ }
}

export function getConfig(): TenantConfig | null {
  return configState.value
}

export function getApiBaseUrl(): string {
  return configState.value?.apiBaseUrl ?? ''
}

export function getApiKey(): string {
  return configState.value?.apiKey ?? ''
}

export function getTenantId(): string {
  return configState.value?.tenantId ?? ''
}

export function getTheme(): 'light' | 'dark' {
  return configState.value?.theme ?? 'light'
}

export function getDevAutoLoginEmail(): string {
  return configState.value?.devAutoLoginEmail?.trim() || 'pat@fakebrains.com'
}

export function isApiConfigured(): boolean {
  return !!(configState.value?.apiBaseUrl && configState.value?.apiKey)
}

export function isTenantMode(): boolean {
  return !!configState.value?.tenantId
}

export function isDemoEnabled(): boolean {
  return configState.value?.demoEnabled === true
}

export function isDevelopment(): boolean {
  return import.meta.env.DEV
}

// ============================================================================
// Session management
// ============================================================================

const SESSION_TOKEN_KEY = 'salesRepApp_sessionToken'
const sessionTokenState = ref<string | null>(null)

export function getSessionToken(): string | null {
  return sessionTokenState.value
}

export function getSessionTokenRef() {
  return readonly(sessionTokenState)
}

export function setSessionToken(token: string): void {
  companySessionStorage.setItem(SESSION_TOKEN_KEY, token)
  sessionTokenState.value = token
}

export function clearSession(): void {
  clearCompanySessionStorage()
  sessionTokenState.value = null
}

export function isAuthenticated(): boolean {
  return !!getSessionToken()
}
